// Identity — the facts that belong to the person rather than to either half of what
// they do on the platform.
//
// Was a collapsed `Accordion` of two `<dl>` grids. Now a `SectionCard` of `Facts`, the
// same idiom the driver record uses one tab over. Two treatments, visually distinct on
// purpose, because they are two different things:
//   - name / phone / email edit through `UserEditDialog`, gated on `edit_user`
//   - the ids and the derived values are read-only
// A uniformly flat grid hid which was which, and hid the permission with it. Every
// value is click-to-copy either way — `Facts` does that, since retyping an id into
// another tool is what these fields are FOR.
//
// Three fields left rather than moved: the Stripe customer and connected-account links
// now live in the page header's ServiceStrip (they were the two most buried links on
// the old page), driver application status is the Driver tab's status section, and
// driver type went to that same section — it writes to the driver record, not the
// user. None is deleted — all are one place better.
import { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  Grid,
  TextField,
  Button,
} from '../../../ui/core';
import { CloseIcon, PersonIcon } from '../../../ui/icons';

import { useForm, useFormState } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useDispatch } from 'react-redux';
import { useToast } from '../../../hooks';
import { editUserById } from '../../../redux/Users/actions';
import { useHasPermission } from '../../../helpers/api/auth/index';
import { SectionCard } from '../../../pages/DriverProfile/components';
import { Facts, TrailingMeta, IconAction } from '../../../pages/UserProfile/parts';

const UserEditDialog = ({ user, onClose }) => {
  const dispatch = useDispatch();
  const t = useToast();
  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: user.firstName,
      middleName: user.middleName,
      lastName: user.lastName,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
    resolver: yupResolver(
      yup.object().shape({
        firstName: yup.string().required('First Name is required'),
        lastName: yup.string().required('Last Name is required'),
        email: yup.string().required('Email is required').email('Email not valid'),
        phoneNumber: yup
          .string()
          .required('Phone Number is required')
          .test({
            name: 'phone',
            message: 'Phone not valid',
            test: (value) => value && value.startsWith('+'),
          }),
      }),
    ),
  });
  const { isDirty, isValid, isSubmitting } = useFormState({ control: form.control });

  const onSubmit = async (data) => {
    const { success } = await editUserById(user.id, data)(dispatch);
    success ? t.success('User data updated') : t.error('User data not saved');
    success && onClose();
  };

  useEffect(() => {
    form.reset({
      firstName: user.firstName,
      middleName: user.middleName,
      lastName: user.lastName,
      email: user.email,
      phoneNumber: user.phoneNumber,
    });
  }, [user, form]);

  return (
    <Dialog open onClose={onClose}>
      <Box display="flex" alignItems="center" justifyContent="space-between" padding="32px 32px 0 32px">
        <DialogTitle sx={{ padding: 0 }}>Edit user</DialogTitle>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box component="form" onSubmit={form.handleSubmit((data) => onSubmit(data))}>
        <Box padding="32px 32px 32px 32px">
          <Grid container spacing="16px">
            <Grid item xs={6}>
              <TextField label="First Name" fullWidth control={form.control} name="firstName" />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Middle Name" fullWidth control={form.control} name="middleName" />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Last Name" fullWidth control={form.control} name="lastName" />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Email" fullWidth control={form.control} name="email" />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Phone Number" fullWidth control={form.control} name="phoneNumber" />
            </Grid>
          </Grid>
        </Box>
        <Button
          type="submit"
          disabled={!(isValid && isDirty)}
          loading={isSubmitting}
          variant="contained"
          size="large"
          disableElevation
          sx={{ width: '100%', padding: '20px', borderRadius: '0 0 4px 4px' }}
        >
          Save
        </Button>
      </Box>
    </Dialog>
  );
};

export default function UserDetailsCard({ user }) {
  const isDriver = !!user.driver_id;
  const [showDialog, setShowDialog] = useState(false);
  const hasEditUserPermission = useHasPermission('edit_user');

  // Undefined rather than false when the permission is missing: `Facts` renders the
  // hover pencil off the presence of `onEdit`, so the read-only grid is the same code
  // path as the editable one rather than a second branch that could drift from it.
  const onEdit = hasEditUserPermission ? () => setShowDialog(true) : undefined;

  // Frequently a truthy object whose own fields are empty, which is why this filters
  // rather than interpolates — the same trap the page header hit.
  const homeCity = [user.homeCity?.cityName, user.homeCity?.stateAbbr].filter(Boolean).join(', ');

  const facts = [
    { label: 'Legal name', value: [user.firstName, user.middleName, user.lastName].filter(Boolean).join(' '), onEdit },
    { label: 'Phone', value: user.phoneNumber, onEdit },
    { label: 'Email', value: user.email, onEdit },
    { label: 'Home city', value: homeCity },
    { label: 'User ID', value: user.id },
    { label: 'Rider ID', value: user.rider_id },
    ...(isDriver ? [{ label: 'Driver ID', value: user.driver_id }] : []),
  ];

  return (
    <>
      <SectionCard
        title="Identity"
        icon={<PersonIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
        meta={
          hasEditUserPermission && (
            <TrailingMeta>
              <IconAction kind="edit" label="Edit user details" onClick={() => setShowDialog(true)} />
            </TrailingMeta>
          )
        }
      >
        <Facts items={facts} />
      </SectionCard>
      {showDialog && <UserEditDialog user={user} onClose={() => setShowDialog(false)} />}
    </>
  );
}
